import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ClienteService, GeradorService } from "@/services/StorageService";
import { Cliente, Gerador } from "@/types";
import { ArrowLeft, Trash2, FileEdit, User, MapPin, Building, Wallet, Percent, CircleDollarSign, ArrowRight, BarChart2, Zap, Edit } from "lucide-react";
import ClienteEditForm from "@/components/clientes/ClienteEditForm";
import { TarifaEdicaoDialog } from "@/components/clientes/TarifaEdicaoDialog";
import { Progress } from "@/components/ui/progress";
import { Sun } from "lucide-react";

const ClienteDetalhes = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [gerador, setGerador] = useState<Gerador | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isTarifaDialogOpen, setIsTarifaDialogOpen] = useState(false);
  
  useEffect(() => {
    if (id) {
      const clienteEncontrado = ClienteService.getById(id);
      if (clienteEncontrado) {
        setCliente(clienteEncontrado);
        
        const geradorEncontrado = GeradorService.getById(clienteEncontrado.geradorId);
        if (geradorEncontrado) {
          setGerador(geradorEncontrado);
        }
      } else {
        toast.error("Cliente não encontrado");
        navigate("/clientes");
      }
    }
  }, [id, navigate]);
  
  const handleDelete = () => {
    if (id) {
      const success = ClienteService.delete(id);
      if (success) {
        toast.success("Cliente excluído com sucesso");
        navigate("/clientes");
      } else {
        toast.error("Erro ao excluir cliente");
      }
    }
  };
  
  const handleSaveEdit = (updatedCliente: Cliente) => {
    setCliente(updatedCliente);
    setIsEditing(false);
    toast.success("Cliente atualizado com sucesso");
  };
  
  const handleTarifaUpdated = (updatedCliente: Cliente) => {
    setCliente(updatedCliente);
    toast.success("Tarifa atualizada com sucesso");
  };
  
  const consumoMedio = cliente ? cliente.gastoEnergia / cliente.tarifaEnergia : 0;
  const tarifaGerador = cliente && cliente.tarifaGerador !== undefined 
    ? cliente.tarifaGerador 
    : gerador ? gerador.tarifaVenda : 0;
    
  const economiaEstimada = cliente 
    ? consumoMedio * (cliente.tarifaEnergia - tarifaGerador) 
    : 0;
  
  const percentualEconomia = cliente && cliente.gastoEnergia > 0 
    ? (economiaEstimada / cliente.gastoEnergia) * 100 
    : 0;
  
  const percentualEnergiaAdquirida = cliente && gerador && gerador.geracaoEstimada > 0
    ? (consumoMedio / gerador.geracaoEstimada) * 100
    : 0;
  
  const percentualCreditos = cliente && gerador && gerador.creditosDisponiveis > 0
    ? (consumoMedio / gerador.creditosDisponiveis) * 100
    : 0;
  
  if (!cliente) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-muted-foreground">Carregando cliente...</p>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (isEditing) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <ClienteEditForm 
            cliente={cliente} 
            onSave={handleSaveEdit} 
            onCancel={() => setIsEditing(false)} 
          />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate("/clientes")} 
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Voltar
            </Button>
            <h1 className="text-2xl font-bold text-blue-700">Detalhes do Cliente</h1>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="text-blue-700 border-blue-200 hover:bg-blue-50" 
              onClick={() => setIsEditing(true)}
            >
              <FileEdit className="mr-2 h-4 w-4" />
              Editar
            </Button>
            <Button 
              variant="destructive"
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-white border-b border-blue-100">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl text-blue-800">
                      {cliente.nome}
                    </CardTitle>
                    <CardDescription>
                      {cliente.tipo === 'energia' ? 'Cliente de Energia' : 'Cliente Solar'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-100 px-3 py-1 rounded-full text-xs font-medium text-blue-800">
                    <User className="h-3 w-3" />
                    {cliente.documento}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-blue-100">
                  <div className="p-4 space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                        <MapPin className="h-4 w-4 mr-2 text-blue-500" />
                        Endereço
                      </h3>
                      <div className="text-sm space-y-1 text-gray-600">
                        <p>{cliente.logradouro}</p>
                        <p>{cliente.bairro}, {cliente.cidade} - {cliente.estado}</p>
                        <p>CEP: {cliente.cep}</p>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                        <Building className="h-4 w-4 mr-2 text-blue-500" />
                        Dados da Concessionária
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Concessionária</p>
                          <p className="font-medium">{cliente.concessionaria}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Nº Cliente</p>
                          <p className="font-medium">{cliente.numeroCliente}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Nº Instalação</p>
                          <p className="font-medium">{cliente.numeroInstalacao}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-sm font-medium text-blue-800 flex items-center">
                          <Wallet className="h-4 w-4 mr-2 text-blue-500" />
                          Dados de Consumo
                        </h3>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          onClick={() => setIsTarifaDialogOpen(true)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Editar Tarifa
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Gasto Mensal</p>
                          <p className="font-medium">R$ {cliente.gastoEnergia.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Tarifa Atual</p>
                          <p className="font-medium flex items-center">
                            R$ {cliente.tarifaEnergia.toFixed(4)}/kWh
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Consumo Médio</p>
                          <p className="font-medium">{consumoMedio.toFixed(2)} kWh/mês</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Gasto Anual</p>
                          <p className="font-medium">R$ {(cliente.gastoEnergia * 12).toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                    
                    {gerador && (
                      <div>
                        <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                          <Percent className="h-4 w-4 mr-2 text-blue-500" />
                          Análise Financeira
                        </h3>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Economia Mensal</p>
                            <p className="font-medium text-green-600">R$ {economiaEstimada.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Economia Anual</p>
                            <p className="font-medium text-green-600">R$ {(economiaEstimada * 12).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">% de Economia</p>
                            <p className="font-medium text-green-600">{percentualEconomia.toFixed(2)}%</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {gerador && (
              <Card className="border-green-200">
                <CardHeader className="bg-gradient-to-r from-green-50 to-white border-b border-green-100">
                  <CardTitle className="text-lg text-green-800 flex items-center">
                    <BarChart2 className="h-5 w-5 mr-2 text-green-600" />
                    Análise de Consumo e Capacidade
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between items-center text-sm mb-1">
                          <span className="text-muted-foreground">Consumo vs Capacidade do Gerador:</span>
                          <span className="font-medium text-green-700">{percentualEnergiaAdquirida.toFixed(1)}%</span>
                        </div>
                        <Progress 
                          value={Math.min(percentualEnergiaAdquirida, 100)} 
                          className="h-2"
                          indicatorClassName={percentualEnergiaAdquirida > 80 ? "bg-amber-500" : "bg-green-500"}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {consumoMedio.toFixed(0)} kWh de {gerador.geracaoEstimada.toFixed(0)} kWh totais
                        </p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center text-sm mb-1">
                          <span className="text-muted-foreground">Utilização de Créditos Disponíveis:</span>
                          <span className="font-medium text-green-700">{percentualCreditos.toFixed(1)}%</span>
                        </div>
                        <Progress 
                          value={Math.min(percentualCreditos, 100)} 
                          className="h-2"
                          indicatorClassName={percentualCreditos > 90 ? "bg-red-500" : percentualCreditos > 70 ? "bg-amber-500" : "bg-green-500"}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {consumoMedio.toFixed(0)} kWh de {gerador.creditosDisponiveis.toFixed(0)} kWh disponíveis
                        </p>
                      </div>
                      
                      <div className="bg-green-50 p-3 rounded-md mt-4">
                        <p className="text-sm font-medium text-green-800 mb-2">Fluxo de Energia Mensal</p>
                        <div className="flex items-center justify-between text-sm">
                          <div className="text-center">
                            <div className="bg-white h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-1 shadow-sm">
                              <Zap className="h-6 w-6 text-amber-500" />
                            </div>
                            <span className="text-muted-foreground text-xs">Gerador</span>
                          </div>
                          <div className="flex-1 flex items-center justify-center max-w-[100px]">
                            <ArrowRight className="h-5 w-5 text-green-500" />
                          </div>
                          <div className="text-center">
                            <div className="bg-white h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-1 shadow-sm">
                              <User className="h-6 w-6 text-blue-500" />
                            </div>
                            <span className="text-muted-foreground text-xs">Cliente</span>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-center text-muted-foreground">
                          {consumoMedio.toFixed(0)} kWh transferidos mensalmente
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="bg-green-50 p-4 rounded-md">
                        <h3 className="text-sm font-medium text-green-800 mb-3 flex items-center">
                          <CircleDollarSign className="h-4 w-4 mr-2 text-green-600" />
                          Dados do Contrato
                        </h3>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Tarifa do Cliente</p>
                            <p className="font-medium">R$ {cliente.tarifaEnergia.toFixed(4)}/kWh</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Tarifa do Gerador</p>
                            <p className="font-medium">
                              R$ {tarifaGerador.toFixed(4)}/kWh
                              {cliente.tarifaGerador !== undefined && (
                                <span className="text-xs text-blue-600 ml-1">(Personalizada)</span>
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Diferença Tarifária</p>
                            <p className="font-medium text-green-700">
                              R$ {(cliente.tarifaEnergia - tarifaGerador).toFixed(4)}/kWh
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Valor Mensal Pago</p>
                            <p className="font-medium">
                              R$ {(consumoMedio * tarifaGerador).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-white border border-green-200 rounded-md">
                        <h3 className="text-sm font-medium text-green-800 mb-2">Resumo Financeiro Anual</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Gasto sem energia renovável:</span>
                            <span className="font-medium">R$ {(cliente.gastoEnergia * 12).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Gasto com energia renovável:</span>
                            <span className="font-medium">R$ {((cliente.gastoEnergia - economiaEstimada) * 12).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-medium text-green-700 pt-2 border-t border-green-100">
                            <span>Economia anual:</span>
                            <span>R$ {(economiaEstimada * 12).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          <div className="space-y-6">
            {gerador && (
              <Card className="border-amber-200">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-white border-b border-amber-100">
                  <CardTitle className="text-base text-amber-800 flex items-center">
                    <Sun className="h-5 w-5 mr-2 text-amber-600" />
                    Gerador Associado
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-amber-800 mb-2">Dados do Gerador</h3>
                      <div className="bg-white p-3 rounded-md border border-amber-100">
                        <p className="font-medium">{gerador.nomeTitular}</p>
                        <p className="text-sm text-muted-foreground">{gerador.cidade}/{gerador.estado}</p>
                        <div className="mt-2 pt-2 border-t border-amber-50 grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Potência</p>
                            <p className="font-medium">{gerador.potenciaInstalada} kWp</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Geração</p>
                            <p className="font-medium">{gerador.geracaoEstimada} kWh/mês</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Concessionária</p>
                            <p className="font-medium">{gerador.concessionaria}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-2">
                      <Button 
                        variant="outline" 
                        className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                        asChild
                      >
                        <Link to={`/geradores/${gerador.id}`}>
                          Ver detalhes do gerador
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card className="border-purple-200">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-white border-b border-purple-100">
                <CardTitle className="text-base text-purple-800">Ações</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-2">
                  <Button 
                    variant="outline"
                    className="w-full justify-start text-purple-700 border-purple-200 hover:bg-purple-50"
                    onClick={() => setIsTarifaDialogOpen(true)}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Editar Tarifa de Energia
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full justify-start text-blue-700 border-blue-200 hover:bg-blue-50"
                    onClick={() => setIsEditing(true)}
                  >
                    <FileEdit className="mr-2 h-4 w-4" />
                    Editar Cliente
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full justify-start text-red-700 border-red-200 hover:bg-red-50"
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Excluir Cliente
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. Isso excluirá permanentemente este cliente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Sim, excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {cliente && gerador && (
          <TarifaEdicaoDialog 
            isOpen={isTarifaDialogOpen}
            onClose={() => setIsTarifaDialogOpen(false)}
            cliente={cliente}
            gerador={gerador}
            onTarifaUpdated={handleTarifaUpdated}
          />
        )}
      </div>
    </Layout>
  );
};

export default ClienteDetalhes;
